import { formatRowCount } from '../../common/utils/formatRowCount';
import type { QueryExecutionStats, QueryResultSet } from '../../../../types/queryExecution';

/** The row figures one successful Query Editor run reports. */
export type ExecutionRowSummary = {
  /** Rows the run's result sets returned — the status bar's row counter. */
  readonly returnedRowCount: number;
  readonly totalRowCount: number | undefined;
  readonly truncated: boolean;
  /** Rows its data-modifying statements changed (`QueryExecutionStats.affectedRowCount`). */
  readonly affectedRowCount: number | undefined;
  /** At least one result set carries columns, i.e. something was returned to show. */
  readonly hasReturnedResultSet: boolean;
};

const carriesColumns = (resultSet: QueryResultSet): boolean => (resultSet.columns?.length ?? 0) > 0;

/**
 * Separate what a run RETURNED from what it CHANGED.
 *
 * Without an affected count the stats are taken as they are — a SELECT-only
 * run reads exactly as it always has. With one, the returned figure is
 * recounted from the result sets that carry columns: PostgreSQL and Oracle
 * list each DML statement as a column-less result set and fold its count into
 * `returnedRowCount` / `totalRowCount`, which would report an UPDATE's rows as
 * "returned" (and a status bar of "0/5 rows" once they were split out). A
 * truncated run keeps the stats' own figures — its result sets only hold the
 * fetched window.
 */
export function summarizeExecutionRows(
  stats: QueryExecutionStats | undefined,
  resultSets: readonly QueryResultSet[],
): ExecutionRowSummary {
  const truncated = stats?.truncated === true;
  const affectedRowCount = stats?.affectedRowCount;
  const hasReturnedResultSet = resultSets.some(carriesColumns);
  if (affectedRowCount === undefined || truncated) {
    return {
      returnedRowCount: stats?.returnedRowCount ?? 0,
      totalRowCount: stats?.totalRowCount,
      truncated,
      affectedRowCount,
      hasReturnedResultSet,
    };
  }
  const returnedRowCount = resultSets
    .filter(carriesColumns)
    .reduce((sum, resultSet) => sum + (resultSet.rowCount ?? resultSet.rows?.length ?? 0), 0);
  return { returnedRowCount, totalRowCount: returnedRowCount, truncated, affectedRowCount, hasReturnedResultSet };
}

/**
 * The row part of the success line: `3 rows returned`, `Showing 1,000 of
 * 5,000 rows`, `8 rows affected` (nothing returned) or `3 rows returned,
 * 8 rows affected` (both).
 */
export function formatExecutionRowSummary(summary: ExecutionRowSummary): string {
  const returned = summary.truncated && typeof summary.totalRowCount === 'number'
    ? `Showing ${summary.returnedRowCount.toLocaleString()} of ${formatRowCount(summary.totalRowCount)}`
    : `${formatRowCount(summary.returnedRowCount)} returned`;
  if (summary.affectedRowCount === undefined) return returned;
  const affected = `${formatRowCount(summary.affectedRowCount)} affected`;
  return summary.hasReturnedResultSet ? `${returned}, ${affected}` : affected;
}
